import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Button, Divider, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import TermsAndConditionsInfo from '../../../components/TermsAndConditionsInfo';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';

import { getAuthPath, PATH_PATIENT_AUTH } from '../../../routes/paths';
import { getSection } from '../../../utils/getSection';

// ----------------------------------------------------------------------

export default function LoginForm({ resetPasswordPath, registerPath }) {
  const { login, resendSignUp } = useAuth();
  const section = getSection(window.location.pathname)
  const PATH = getAuthPath(section)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: false,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password, section);
    } catch (error) {
      console.error(error);
      // reset();
      // if error is user is unconfirmed
      if (error.code === 'UserNotConfirmedException') {
        sessionStorage.setItem('verify-code', data.email);

        try {
          await resendSignUp(data.email);
          enqueueSnackbar('Please verify your email.', { variant: 'error' });
          navigate(PATH.verify);
        } catch (err) {
          if (err.code === 'LimitExceededException') {
            setError('afterSubmit', { ...err, message: err.message });
          }
        }

      }


      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label="Remember me" />
        <Link component={RouterLink} variant="subtitle2" to={resetPasswordPath}>
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        variant="contained"
        type="submit"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>

      <TermsAndConditionsInfo section={section} />

      {!['employee']?.includes(section) && <>
        <Divider sx={{ mt: 3, mb: 4 }} />

        <Link
          component={RouterLink}
          to={PATH_PATIENT_AUTH.register}
          variant="subtitle2"
          sx={{ '&:hover': { textDecoration: 'none' } }}
        >
          <Button
            fullWidth
            variant="outlined"
            size="large"
          >Create Account</Button>
        </Link>
      </>}
    </FormProvider>
  );
}
